import type { UniverseSearchModel } from '../UniverseCreationModel';
import { PresetSelectionModel } from './PresetSelectionModel';
import type { PresetSelectionModelInstance } from './PresetSelectionModel';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { computed, signal } from '@preact/signals-react';
import type { ConcreteIndicator, EnumeratedReturnValue } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { SelfAliasIndicatorFormula } from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { FormulaService } from 'src/services/FormulaService';

export type SetConditionValue = number | string | boolean;

export interface SetConditionInit {
    id: string;
    enabled: boolean;
    indicator: ConcreteIndicator;
    values: ReadonlyArray<SetConditionValue>;
    enumeratedValues?: ReadonlyArray<EnumeratedReturnValue>;
    selection?: SelfAliasIndicatorFormula;
    searchModel: UniverseSearchModel;
    formulaService: FormulaService;
}

interface SetConditionState {
    readonly enabled: boolean;
    readonly indicator: ConcreteIndicator;
    readonly values: ReadonlyArray<SetConditionValue>;
    readonly enumeratedValues: ReadonlyArray<EnumeratedReturnValue> | undefined;
}

type SetConditionAction =
    | { type: 'SET_ENABLED'; enabled: boolean }
    | { type: 'SET_VALUES'; values: ReadonlyArray<SetConditionValue> }
    | { type: 'ADD_VALUE'; value: SetConditionValue }
    | { type: 'REMOVE_VALUE'; value: SetConditionValue };

function reducer(state: SetConditionState, action: SetConditionAction): SetConditionState {
    switch (action.type) {
        case 'SET_ENABLED':
            if (state.enabled === action.enabled) return state;
            return { ...state, enabled: action.enabled };
        case 'SET_VALUES':
            if (state.values === action.values) return state;
            return { ...state, values: action.values };
        case 'ADD_VALUE':
            if (state.values.includes(action.value)) return state;
            return { ...state, values: [...state.values, action.value] };
        case 'REMOVE_VALUE': {
            if (!state.values.includes(action.value)) return state;
            return { ...state, values: state.values.filter((v) => v !== action.value) };
        }
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Set-membership criterion: keeps symbols whose `indicator` value matches
 * any of the chosen `values`. Acts like a multi-select; `addValue` and
 * `removeValue` enforce uniqueness, `setValues` replaces the whole set.
 *
 * Use for indicators backed by enumerated return values (a fixed picker)
 * or free-form string indicators (e.g. ad-hoc sector tags). Values are
 * `string | number | boolean` to cover all enumerated types.
 *
 * The current preset selection lives on `presetSelection`; the `selection`
 * signal exposed here is a thin computed projection used for wire
 * serialization.
 */
export class SetConditionModel implements Disposable {
    readonly type = 'set' as const;

    readonly #state: Signal<SetConditionState>;

    readonly id: string;
    readonly enabled: ReadonlySignal<boolean>;
    readonly indicator: ReadonlySignal<ConcreteIndicator>;
    readonly values: ReadonlySignal<ReadonlyArray<SetConditionValue>>;
    readonly selection: ReadonlySignal<SelfAliasIndicatorFormula | undefined>;
    readonly presetSelection: PresetSelectionModelInstance;
    /**
     * Scalar values eligible for selection in this condition. The caller pre-
     * filters by view-model concerns (e.g. backtest support) and hands the
     * resulting enum entries in via `enumeratedValues`; this model narrows to
     * the `string | number | boolean` subset it actually surfaces. `null` when
     * no enumerated entries were provided.
     */
    readonly availableEnumeratedValues: ReadonlySignal<SetConditionValue[] | null>;
    readonly validationErrors: ReadonlySignal<string[]>;
    readonly isValid: ReadonlySignal<boolean>;

    constructor(init: SetConditionInit) {
        this.id = init.id;
        this.presetSelection = new PresetSelectionModel({
            indicator: init.indicator,
            initialSelection: init.selection,
            searchModel: init.searchModel,
            formulaService: init.formulaService,
        });
        this.#state = signal<SetConditionState>({
            enabled: init.enabled,
            indicator: init.indicator,
            values: init.values,
            enumeratedValues: init.enumeratedValues,
        });

        this.enabled = computed(() => this.#state.value.enabled);
        this.indicator = computed(() => this.#state.value.indicator);
        this.values = computed(() => this.#state.value.values);
        this.selection = computed(() => this.presetSelection.selectedFormula.value);
        this.availableEnumeratedValues = computed(() => {
            const entries = this.#state.value.enumeratedValues;
            if (entries == null) return null;
            const out: SetConditionValue[] = [];
            for (const entry of entries) {
                const value = entry.value;
                if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    out.push(value);
                }
            }
            return out;
        });
        this.validationErrors = computed(() => {
            const s = this.#state.value;
            const errors: string[] = [];
            if (s.values.length === 0) {
                errors.push(`Select at least one ${s.indicator.name} value`);
            }
            return errors;
        });
        this.isValid = computed(() => this.validationErrors.value.length === 0);
    }

    #dispatch(action: SetConditionAction): void {
        this.#state.value = reducer(this.#state.value, action);
    }

    setEnabled(enabled: boolean): void {
        this.#dispatch({ type: 'SET_ENABLED', enabled });
    }

    setValues(values: ReadonlyArray<SetConditionValue>): void {
        this.#dispatch({ type: 'SET_VALUES', values });
    }

    addValue(value: SetConditionValue): void {
        this.#dispatch({ type: 'ADD_VALUE', value });
    }

    removeValue(value: SetConditionValue): void {
        this.#dispatch({ type: 'REMOVE_VALUE', value });
    }

    [Symbol.dispose](): void {
        this.presetSelection[Symbol.dispose]();
    }
}
